import { useState } from 'react';
import {
  DeleteOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Tooltip, Typography, Upload, message } from 'antd';
import { RcFile } from 'antd/es/upload';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import {
  UserAccountNotificationSettingsResponse,
  UserAccountProfileResponse,
  useGetMyAccount,
  useUpdateMyAccount,
} from '@/shared/api';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Col, Row } from '@/shared/ui';
import { toBase64 } from '@/shared/utils';
import { captureError } from '@/shared/utils/error-reporting';
import { PageLayout } from '@/widgets/page-layout';
import { ProfilePageHeader } from './ProfilePageHeader';
import { ProfileTabs } from './ProfileTabs';

const { Title, Text } = Typography;

export const Profile = () => {
  const { theme } = useThemeContext();
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();

  const [showBtnOverlay, setShowBtnOverlay] = useState(false);

  const { data: profileData, isLoading: profileDataLoading } = useGetMyAccount<{
    profile: UserAccountProfileResponse;
    notificationSettings: UserAccountNotificationSettingsResponse;
  }>({});
  const { mutate: updateAccount } = useUpdateMyAccount();

  const beforeUpload = async (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'You can only upload JPG/PNG file!',
      });
    }
    const isLt1M = file.size / 1024 / 1024 < 0.51;
    if (!isLt1M) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'Image must be smaller than 500KB!',
      });
    }
    if (isLt1M && isJpgOrPng) {
      await toBase64(file).then((base64) => {
        try {
          updateAccount(
            {
              body: {
                profile: {
                  avatarImage: base64 as string,
                },
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: ['accounts', 'my-profile'],
                });
              },
              onError: (error: any) => {
                message.error('Failed to upload image');
                captureError(error, {
                  module: 'profile_on_error_update_account',
                  action: 'update_account',
                });
              },
            },
          );
        } catch (error) {
          message.error('Failed to upload image');
          captureError(error, {
            module: 'profile_on_error_update_account',
            action: 'update_account',
          });
        }
      });
    }
    return false;
  };

  const handleProfileImageRemove = async (
    e:
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    try {
      updateAccount(
        {
          body: {
            profile: {
              avatarImage: '',
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['accounts', 'my-profile'],
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'profile_on_error_handle_profile_image_remove',
        action: 'handle_profile_image_remove',
      });
    }
  };

  return (
    <PageLayout
      headerContent={<ProfilePageHeader />}
      contentStyle={{ padding: 0, paddingTop: 0 }}
    >
      <Banner />
      <Wrapper alignItems="flex-start">
        <Row gap={30} style={{ marginBottom: 30 }}>
          <ProfileImageWrapper
            onMouseEnter={() => setShowBtnOverlay(true)}
            onMouseLeave={() => setShowBtnOverlay(false)}
            style={{
              border: `4px solid ${themeConfig[theme].colors.layoutBg}`,
            }}
          >
            <UploadWrapper
              listType="picture-circle"
              maxCount={1}
              beforeUpload={beforeUpload}
            >
              <>
                {showBtnOverlay && (
                  <>
                    <TransparentBackground
                      style={{
                        background:
                          themeConfig[theme].colors.contentBgSecondary,
                      }}
                    />
                    <ButtonsWrapper gap={15}>
                      <Tooltip title="Upload new image">
                        <IconButton type="text" icon={<UploadOutlined />} />
                      </Tooltip>
                      {profileData?.profile.avatarImage && (
                        <Tooltip title="Remove image">
                          <IconButton
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={handleProfileImageRemove}
                          />
                        </Tooltip>
                      )}
                    </ButtonsWrapper>
                  </>
                )}
                <Avatar
                  icon={
                    !profileData?.profile.avatarImage &&
                    !profileDataLoading && (
                      <UserOutlined
                        style={{
                          fontSize: 72,
                          lineHeight: '72px',
                          color: themeConfig[theme].colors.text,
                        }}
                      />
                    )
                  }
                  src={profileData?.profile.avatarImage}
                  style={{
                    backgroundColor: themeConfig[theme].colors.bgBase,
                    color: '#000',
                    border: `1px solid ${themeConfig[theme].colors.text}`,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </>
            </UploadWrapper>
          </ProfileImageWrapper>
          <Col alignItems="flex-start">
            <Title style={{ marginBottom: '.1em' }}>Settings</Title>
            <Text style={{ opacity: 0.8 }}>{profileData?.profile.email}</Text>
          </Col>
        </Row>
        <ProfileTabs />
      </Wrapper>
    </PageLayout>
  );
};

const Wrapper = styled(Col)`
  width: 100%;
  padding: 0 30px 100px;
  max-width: 1024px;
  margin: 0 auto;
`;

const Banner = styled.div`
  height: 20%;
  background: #000046; /* fallback for old browsers */
  background: -webkit-linear-gradient(
    224deg,
    #8117a7 0%,
    #1d6ab9 49.48%,
    #82e580 100%
  );
  background: linear-gradient(224deg, #8117a7 0%, #1d6ab9 49.48%, #82e580 100%);
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  margin-top: -35px;
  position: relative;
  overflow: hidden;
`;

const UploadWrapper = styled(Upload)`
  width: 150px !important;
  height: 150px !important;

  .ant-upload.ant-upload-select {
    width: 142px !important;
    height: 142px !important;
    border: none !important;
  }
  .ant-upload-list-item-container {
    display: none !important;
  }
`;

const ButtonsWrapper = styled(Row)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  justify-content: center;
`;

const TransparentBackground = styled.div`
  opacity: 0.8;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
`;

const IconButton = styled(Button)`
  border-radius: 50%;
`;
